import {createStyles, Textarea} from '@mantine/core';
import React, {useState} from 'react';

import {MAX_AUTOMATION_TEMPLATE_DESCRIPTION_LENGTH} from 'analysis-tool/AnalysisToolConstants';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';

export interface DuplicateAnalysisTemplateModalProps {
  sourceAnalysisTemplateName: string;
  sourceAnalysisTemplateCollectionId: string;
  sourceTemplateIsPublished: boolean;
  sourceTemplateDescription: string | null;
  actionOrigin?: ActionOrigin;
  onRequestClose: () => void;
  onRequestSubmit: (params: {
    name: string;
    templateCollectionId: string;
    description: string | null;
  }) => Promise<void>;
}

const useStyles = createStyles(() => ({
  input: {
    resize: 'vertical',
  },
}));

export default function DuplicateAnalysisTemplateModal(
  props: DuplicateAnalysisTemplateModalProps
): JSX.Element {
  const [name, setName] = useState(`Copy of ${props.sourceAnalysisTemplateName}`);
  const [templateCollectionId, setTemplateCollectionId] = useState<string>(
    props.sourceAnalysisTemplateCollectionId
  );
  const [description, setDescription] = useState(props.sourceTemplateDescription);

  const {classes} = useStyles();

  const trimmedName = name.trim();
  const isNameBlank = trimmedName === '';

  const normalizedDescription = description?.trim() || null;
  const descriptionLength = normalizedDescription?.length ?? 0;
  const isDescriptionTooLong = descriptionLength > MAX_AUTOMATION_TEMPLATE_DESCRIPTION_LENGTH;

  function isSubmitDisabled(): boolean {
    return isNameBlank || isDescriptionTooLong;
  }

  return (
    <Modal
      title="Duplicate template"
      buttons={[
        {
          text: 'Duplicate',
          submit: true,
          disabled: isSubmitDisabled(),
          tooltipTitle: isNameBlank
            ? `Template name must be non-empty.`
            : isDescriptionTooLong
              ? `Description must be at most ${MAX_AUTOMATION_TEMPLATE_DESCRIPTION_LENGTH} characters.`
              : null,
        },
      ]}
      onRequestSubmit={async () => {
        await props.onRequestSubmit({
          name: trimmedName,
          templateCollectionId,
          description: normalizedDescription,
        });
      }}
      onRequestClose={() => {
        props.onRequestClose();
      }}
      analyticsModule={ANALYTICS_MODULE.ANALYSIS_TOOL}
      analyticsModalType="duplicate_analysis_template"
      analyticsMetadata={{
        actionOrigin: props.actionOrigin,
        sourceTemplateStatus: props.sourceTemplateIsPublished ? 'published' : 'draft',
      }}
      dataTestAttrs={{component: 'DuplicateAnalysisTemplateModal', element: 'root'}}
    >
      <FormGroup>
        <Row>
          <Col md={6}>
            <Label>Name</Label>
            <TextInput
              value={name}
              hasError={isNameBlank}
              onChange={(e) => {
                setName(e.target.value);
              }}
              autoFocus={true}
              {...getTestProps({component: 'DuplicateAnalysisTemplateModal', element: 'nameInput'})}
            />
          </Col>
          <Col md={6}>
            <Label>Template collection</Label>
            <TemplateCollectionSelector
              searchable={true}
              value={templateCollectionId}
              onChange={(collection) => {
                setTemplateCollectionId(collection.id);
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Textarea
              // Mantine's Textarea sets `resize: none`; the legacy `.modal-body textarea` rule currently
              // overrides it, so we set it explicitly to keep vertical resizing regardless of the modal wrapper.
              classNames={{input: classes.input}}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              label="Description"
              description={`${descriptionLength} of ${MAX_AUTOMATION_TEMPLATE_DESCRIPTION_LENGTH} allowed characters.`}
              error={isDescriptionTooLong}
              value={description ?? ''}
              onChange={(e) => {
                setDescription(e.currentTarget.value);
              }}
            />
          </Col>
        </Row>
      </FormGroup>
    </Modal>
  );
}
